import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  BarChart3, ExternalLink, Link2, Copy, Trash2, Plus, Globe,
  TrendingUp, Users, Eye, Sparkles, Lightbulb,
} from "lucide-react";
import { toast } from "sonner";

const PROD_URL = "https://hopechest.organizedhope.online";
const POSTHOG_PROJECT = "Hope Chest";
const POSTHOG_BASE = "https://us.posthog.com";

const STORE_KEY = "hc_campaign_links";

// Common values — kept short to encourage consistency in PostHog reports
const SOURCE_PRESETS = ["facebook", "instagram", "twitter", "pinterest", "tiktok", "linkedin", "youtube", "reddit", "google", "bing", "email", "newsletter", "podcast", "press", "qr-code", "business-card"];
const MEDIUM_PRESETS = ["social", "post", "story", "reel", "comment", "bio", "email", "cpc", "organic", "referral", "qr", "print"];

function loadSaved() {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY) || "[]");
  } catch (e) {
    return [];
  }
}

function persist(arr) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(arr.slice(0, 50)));
  } catch (e) { /* quota — ignore */ }
}

function buildUrl({ base, source, medium, campaign, content, term, landing }) {
  const root = (base || PROD_URL).replace(/\/$/, "");
  const path = landing && landing !== "/" ? (landing.startsWith("/") ? landing : `/${landing}`) : "";
  const params = new URLSearchParams();
  if (source) params.set("utm_source", source.trim().toLowerCase().replace(/\s+/g, "-"));
  if (medium) params.set("utm_medium", medium.trim().toLowerCase().replace(/\s+/g, "-"));
  if (campaign) params.set("utm_campaign", campaign.trim().toLowerCase().replace(/\s+/g, "-"));
  if (content) params.set("utm_content", content.trim().toLowerCase().replace(/\s+/g, "-"));
  if (term) params.set("utm_term", term.trim().toLowerCase().replace(/\s+/g, "-"));
  const qs = params.toString();
  return `${root}${path}${qs ? `?${qs}` : ""}`;
}

export default function AdminAnalyticsTab() {
  // ---- Built-in metrics from our own DB ----
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => setStats({}));
  }, []);

  // ---- UTM builder state ----
  const [form, setForm] = useState({
    base: PROD_URL,
    landing: "/",
    source: "",
    medium: "",
    campaign: "",
    content: "",
    term: "",
    label: "",
  });
  const [saved, setSaved] = useState(loadSaved);

  const built = useMemo(() => buildUrl(form), [form]);
  const isValid = form.source && form.medium && form.campaign;

  const copyToClipboard = async (text, msg = "Link copied") => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(msg);
    } catch (e) {
      toast.error("Could not copy — please copy manually");
    }
  };

  const saveCurrent = () => {
    if (!isValid) {
      toast.error("Source, Medium, and Campaign are required");
      return;
    }
    const entry = {
      id: `lnk_${Date.now()}`,
      url: built,
      label: form.label.trim() || `${form.source} · ${form.campaign}`,
      source: form.source, medium: form.medium, campaign: form.campaign,
      content: form.content, term: form.term, landing: form.landing,
      created_at: new Date().toISOString(),
    };
    const next = [entry, ...saved.filter((s) => s.url !== built)];
    setSaved(next);
    persist(next);
    toast.success("Saved. Click any link below to copy.");
  };

  const removeSaved = (id) => {
    const next = saved.filter((s) => s.id !== id);
    setSaved(next);
    persist(next);
  };

  const resetForm = () => setForm({
    base: PROD_URL, landing: "/", source: "", medium: "", campaign: "",
    content: "", term: "", label: "",
  });

  return (
    <div className="space-y-8" data-testid="admin-analytics-tab">

      {/* ============ HEADER ============ */}
      <div>
        <h2 className="font-display text-2xl mb-1">Analytics &amp; campaigns</h2>
        <p className="text-sm text-[#7A736E]">
          See who visits Hope Chest, where they came from, and what they do — plus build trackable links to send anywhere on the internet.
        </p>
      </div>

      {/* ============ POSTHOG CARD ============ */}
      <div className="hc-card p-7 bg-gradient-to-br from-[#3A5A4A] to-[#2C4538] text-white relative overflow-hidden" data-testid="posthog-card">
        <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[#E8DCC8] opacity-10 blur-3xl" />
        <div className="relative">
          <div className="flex items-start gap-4 mb-4 flex-wrap">
            <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
              <BarChart3 className="w-6 h-6 text-[#E8DCC8]" />
            </div>
            <div className="flex-1">
              <p className="text-xs uppercase tracking-[0.2em] text-[#E8DCC8] mb-1">Live visitor analytics</p>
              <h3 className="font-display text-2xl">Powered by PostHog</h3>
              <p className="text-sm text-white/70 mt-1 max-w-xl">
                Every visitor to Hope Chest is tracked privately — pageviews, sources, devices, and even session recordings you can watch. Use the buttons below to jump straight to the most useful dashboards.
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2.5 mt-6">
            <a
              href={`${POSTHOG_BASE}/web-analytics`}
              target="_blank"
              rel="noreferrer"
              className="rounded-xl bg-white/10 hover:bg-white/15 transition p-3 group"
              data-testid="posthog-web-analytics-link"
            >
              <div className="flex items-center gap-2 mb-1">
                <Globe className="w-4 h-4 text-[#E8DCC8]" />
                <span className="text-xs uppercase tracking-wider text-[#E8DCC8]">Open</span>
              </div>
              <p className="font-medium text-sm">Web Analytics</p>
              <p className="text-xs text-white/60 mt-0.5">Sources, pages, devices, countries</p>
            </a>
            <a
              href={`${POSTHOG_BASE}/activity/live`}
              target="_blank"
              rel="noreferrer"
              className="rounded-xl bg-white/10 hover:bg-white/15 transition p-3 group"
              data-testid="posthog-live-link"
            >
              <div className="flex items-center gap-2 mb-1">
                <Users className="w-4 h-4 text-[#E8DCC8]" />
                <span className="text-xs uppercase tracking-wider text-[#E8DCC8]">Open</span>
              </div>
              <p className="font-medium text-sm">Live visitors</p>
              <p className="text-xs text-white/60 mt-0.5">See people right now</p>
            </a>
            <a
              href={`${POSTHOG_BASE}/replay/recent`}
              target="_blank"
              rel="noreferrer"
              className="rounded-xl bg-white/10 hover:bg-white/15 transition p-3 group"
              data-testid="posthog-replay-link"
            >
              <div className="flex items-center gap-2 mb-1">
                <Eye className="w-4 h-4 text-[#E8DCC8]" />
                <span className="text-xs uppercase tracking-wider text-[#E8DCC8]">Open</span>
              </div>
              <p className="font-medium text-sm">Session replays</p>
              <p className="text-xs text-white/60 mt-0.5">Watch real visits as videos</p>
            </a>
            <a
              href={`${POSTHOG_BASE}/insights`}
              target="_blank"
              rel="noreferrer"
              className="rounded-xl bg-white/10 hover:bg-white/15 transition p-3 group"
              data-testid="posthog-insights-link"
            >
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-4 h-4 text-[#E8DCC8]" />
                <span className="text-xs uppercase tracking-wider text-[#E8DCC8]">Open</span>
              </div>
              <p className="font-medium text-sm">Insights</p>
              <p className="text-xs text-white/60 mt-0.5">Trends, funnels, retention</p>
            </a>
          </div>

          <div className="mt-5 p-3 rounded-xl bg-white/5 border border-white/10 flex items-start gap-2.5">
            <Lightbulb className="w-4 h-4 text-[#E8DCC8] flex-shrink-0 mt-0.5" />
            <p className="text-xs text-white/80 leading-relaxed">
              <strong>To see where your first organic visitor came from:</strong> Click <em>Web Analytics</em> above → <em>Sources</em> tab → filter by date range. Or click <em>Live visitors</em> and sort oldest first.
            </p>
          </div>
        </div>
      </div>

      {/* ============ OUR DB METRICS ============ */}
      <div className="hc-card p-6" data-testid="db-metrics-card">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <h3 className="font-display text-lg">Hope Chest at a glance</h3>
            <p className="text-xs text-[#7A736E]">Live from your database — not delayed.</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Total users" value={stats?.users?.total} testId="metric-total-users" />
          <Stat label="Active trials" value={stats?.active_trials} testId="metric-active-trials" />
          <Stat label="Paid subscribers" value={stats?.users?.paid} testId="metric-paid-subs" />
          <Stat label="Founding members" value={stats?.founding_members?.taken} testId="metric-founding" />
          <Stat label="Hope Chests created" value={stats?.chests_total} testId="metric-total-chests" />
          <Stat label="Total medications" value={stats?.meds_total} testId="metric-meds" />
          <Stat label="Total appointments" value={stats?.appointments_total} testId="metric-appts" />
          <Stat label="Total photos" value={stats?.photos_total} testId="metric-photos" />
        </div>
      </div>

      {/* ============ UTM LINK BUILDER ============ */}
      <div className="hc-card p-7" data-testid="utm-builder-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-[#E8DCC8] flex items-center justify-center">
            <Link2 className="w-5 h-5 text-[#3A5A4A]" />
          </div>
          <div>
            <h3 className="font-display text-xl">Campaign link builder</h3>
            <p className="text-xs text-[#7A736E]">
              Build a trackable URL for every place you share Hope Chest. PostHog will tag every visit with your campaign so you can see what works.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#7A736E]">Where it lands</Label>
            <div className="flex gap-2 mt-1.5">
              <select
                value={form.landing}
                onChange={(e) => setForm({ ...form, landing: e.target.value })}
                className="rounded-xl border border-[#EBE5DF] bg-white px-3 py-2 text-sm focus:border-[#3A5A4A] focus:outline-none flex-1"
                data-testid="utm-landing"
              >
                <option value="/">Landing page (/)</option>
                <option value="/pricing">Pricing (/pricing)</option>
                <option value="/resources">Resources (/resources)</option>
                <option value="/signup">Signup (/signup)</option>
                <option value="/changelog">Changelog (/changelog)</option>
              </select>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#7A736E]">Friendly label (for your eyes only)</Label>
            <Input
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="e.g. Facebook launch post"
              className="rounded-xl mt-1.5"
              data-testid="utm-label"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mt-4">
          <BuilderField
            label="Source"
            required
            hint="Where the link lives (facebook, email, etc.)"
            value={form.source}
            onChange={(v) => setForm({ ...form, source: v })}
            presets={SOURCE_PRESETS}
            testId="utm-source"
          />
          <BuilderField
            label="Medium"
            required
            hint="Type of placement (post, story, email…)"
            value={form.medium}
            onChange={(v) => setForm({ ...form, medium: v })}
            presets={MEDIUM_PRESETS}
            testId="utm-medium"
          />
          <BuilderField
            label="Campaign"
            required
            hint="The specific push (launch, founding-member…)"
            value={form.campaign}
            onChange={(v) => setForm({ ...form, campaign: v })}
            testId="utm-campaign"
          />
          <BuilderField
            label="Content (optional)"
            hint="Distinguish A/B variants (image-v1, button-v2)"
            value={form.content}
            onChange={(v) => setForm({ ...form, content: v })}
            testId="utm-content"
          />
          <BuilderField
            label="Term (optional)"
            hint="Paid-ad keyword (caregiver-app, etc.)"
            value={form.term}
            onChange={(v) => setForm({ ...form, term: v })}
            testId="utm-term"
          />
        </div>

        {/* Preview */}
        <div className="mt-6 p-4 rounded-xl bg-[#F3EFEA] border border-[#EBE5DF]">
          <Label className="text-xs uppercase tracking-wider text-[#7A736E] mb-1.5 block">Your trackable link</Label>
          <div className="flex flex-col sm:flex-row gap-2">
            <code className="flex-1 bg-white border border-[#EBE5DF] rounded-lg px-3 py-2.5 text-xs font-mono text-[#3D3935] break-all" data-testid="utm-preview">
              {isValid ? built : <span className="text-[#7A736E]">Fill in source, medium, and campaign to generate…</span>}
            </code>
            <div className="flex gap-2">
              <Button
                onClick={() => copyToClipboard(built)}
                disabled={!isValid}
                className="rounded-full bg-[#3A5A4A] hover:bg-[#2C4538]"
                data-testid="utm-copy"
              >
                <Copy className="w-4 h-4 mr-1.5" /> Copy
              </Button>
              <Button
                onClick={saveCurrent}
                disabled={!isValid}
                variant="outline"
                className="rounded-full border-[#EBE5DF]"
                data-testid="utm-save"
              >
                <Plus className="w-4 h-4 mr-1.5" /> Save
              </Button>
            </div>
          </div>
          <p className="text-xs text-[#7A736E] mt-2">
            Tip: PostHog logs UTM tags automatically on every pageview. In PostHog → Web Analytics → Sources, you&apos;ll see this campaign appear as soon as someone clicks.
          </p>
        </div>

        {/* Quick reset */}
        <div className="mt-4 flex justify-end">
          <button onClick={resetForm} className="text-xs text-[#7A736E] hover:text-[#3D3935] underline" data-testid="utm-reset">
            Reset form
          </button>
        </div>
      </div>

      {/* ============ SAVED LINKS ============ */}
      <div className="hc-card p-6" data-testid="saved-links-card">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <h3 className="font-display text-lg">Saved campaign links ({saved.length})</h3>
            <p className="text-xs text-[#7A736E]">Stored in this browser. Click a link to copy it again — no need to re-build.</p>
          </div>
        </div>

        {saved.length === 0 ? (
          <div className="text-center py-10 text-sm text-[#7A736E]">
            <Sparkles className="w-6 h-6 mx-auto mb-2 text-[#7A736E]" />
            No saved links yet. Build one above and press <strong>Save</strong> to keep it here.
          </div>
        ) : (
          <ul className="space-y-2">
            {saved.map((s) => (
              <li
                key={s.id}
                className="flex items-start gap-3 p-3 rounded-xl bg-white border border-[#EBE5DF] hover:border-[#3A5A4A] transition"
                data-testid={`saved-link-${s.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <p className="font-medium text-sm text-[#3D3935]">{s.label}</p>
                    <span className="text-[10px] uppercase tracking-wider bg-[#E8DCC8] text-[#3A5A4A] px-1.5 py-0.5 rounded-full">{s.source}</span>
                    <span className="text-[10px] uppercase tracking-wider bg-[#F3EFEA] text-[#7A736E] px-1.5 py-0.5 rounded-full">{s.medium}</span>
                    <span className="text-[10px] uppercase tracking-wider bg-[#F3EFEA] text-[#7A736E] px-1.5 py-0.5 rounded-full">{s.campaign}</span>
                  </div>
                  <p className="font-mono text-xs text-[#7A736E] break-all leading-relaxed">{s.url}</p>
                </div>
                <div className="flex flex-col gap-1.5 flex-shrink-0">
                  <Button size="sm" variant="outline" onClick={() => copyToClipboard(s.url)} className="rounded-full border-[#EBE5DF]" data-testid={`copy-${s.id}`}>
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => removeSaved(s.id)} className="rounded-full border-[#EBE5DF] text-[#C87B67]" data-testid={`delete-${s.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ============ LEARN MORE ============ */}
      <div className="hc-card p-6 bg-[#F3EFEA]" data-testid="learn-more-card">
        <h3 className="font-display text-lg mb-3 flex items-center gap-2">
          <Lightbulb className="w-5 h-5 text-[#D9A441]" /> What good campaigns look like
        </h3>
        <div className="grid md:grid-cols-2 gap-4 text-sm text-[#3D3935]">
          <div>
            <p className="font-medium mb-1">Facebook launch post</p>
            <code className="text-xs text-[#7A736E] block">utm_source=facebook · utm_medium=post · utm_campaign=launch-feb-2026</code>
          </div>
          <div>
            <p className="font-medium mb-1">Email to a caregiver group</p>
            <code className="text-xs text-[#7A736E] block">utm_source=email · utm_medium=newsletter · utm_campaign=parkinson-support-group</code>
          </div>
          <div>
            <p className="font-medium mb-1">Pinterest pin</p>
            <code className="text-xs text-[#7A736E] block">utm_source=pinterest · utm_medium=pin · utm_campaign=caregiver-overwhelm</code>
          </div>
          <div>
            <p className="font-medium mb-1">Business card QR</p>
            <code className="text-xs text-[#7A736E] block">utm_source=qr-code · utm_medium=print · utm_campaign=business-cards</code>
          </div>
        </div>
        <p className="text-xs text-[#7A736E] mt-4">
          Rule of thumb: source = <em>where</em>, medium = <em>what kind</em>, campaign = <em>why</em>. Be consistent so PostHog can group them.
        </p>
      </div>
    </div>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div data-testid={testId}>
      <p className="text-xs uppercase tracking-wider text-[#7A736E]">{label}</p>
      <p className="font-display text-3xl mt-1">{value ?? "—"}</p>
    </div>
  );
}

function BuilderField({ label, hint, value, onChange, presets, required, testId }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-[#7A736E]">
        {label} {required && <span className="text-[#C87B67]">*</span>}
      </Label>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder=""
        className="rounded-xl mt-1.5"
        data-testid={testId}
        list={presets ? `${testId}-presets` : undefined}
      />
      {presets && (
        <datalist id={`${testId}-presets`}>
          {presets.map((p) => <option key={p} value={p} />)}
        </datalist>
      )}
      {hint && <p className="text-[11px] text-[#7A736E] mt-1 leading-snug">{hint}</p>}
    </div>
  );
}
